import React, { useState } from 'react';
import '../styles/TreeSelect.css';

const TreeItem = ({ item, items, level, selectedValue, onSelect, expandedItems, onToggle }) => {
  const hasChildren = items.some(child => child.parentMenuId === item.menuId);
  const isExpanded = expandedItems.includes(item.menuId);
  const indent = '\u00A0\u00A0'.repeat(level);
  
  return (
    <div className="tree-item">
      <div 
        className={`tree-item-content ${selectedValue === item.menuId ? 'selected' : ''}`}
        style={{ paddingLeft: `${level * 20}px` }}
      >
        {hasChildren && (
          <span 
            className={`tree-toggle ${isExpanded ? 'expanded' : ''}`}
            onClick={() => onToggle(item.menuId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle(item.menuId);
              }
            }}
            tabIndex="0"
            role="button"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.menuName}`}
          >
            {isExpanded ? '▼' : '▶'}
          </span>
        )}
        <span 
          className="tree-label"
          onClick={() => onSelect(item.menuId)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(item.menuId);
            }
          }}
          tabIndex="0"
          role="button"
          aria-selected={selectedValue === item.menuId}
          aria-label={`Select ${item.menuName}`}
        >
          {item.menuName}
        </span>
      </div>
      
      {hasChildren && isExpanded && (
        <div className="tree-children">
          {items
            .filter(child => child.parentMenuId === item.menuId)
            .map(child => (
              <TreeItem
                key={child.menuId}
                item={child}
                items={items}
                level={level + 1}
                selectedValue={selectedValue}
                onSelect={onSelect}
                expandedItems={expandedItems}
                onToggle={onToggle}
              />
            ))}
        </div>
      )}
    </div>
  );
};

const TreeSelect = ({ items = [], selectedValue, onChange, placeholder = "Select" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState([]);
  
  const handleToggle = (itemId) => {
    setExpandedItems(prev => 
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };
  
  const handleSelect = (value) => {
    onChange({ target: { value } });
    setIsOpen(false);
  };
  
  const selectedItem = items.find(item => item.menuId === selectedValue);
  
  return (
    <div className="tree-select">
      <div 
        className="tree-select-header"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        tabIndex="0"
        role="button"
        aria-expanded={isOpen}
        aria-label="Toggle tree select dropdown"
      >
        {selectedItem ? selectedItem.menuName : placeholder}
        <span className={`tree-select-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </div>
      
      {isOpen && (
        <div className="tree-select-dropdown">
          {items
            .filter(item => item.parentMenuId === 0)
            .map(item => (
              <TreeItem
                key={item.menuId}
                item={item}
                items={items}
                level={0}
                selectedValue={selectedValue}
                onSelect={handleSelect}
                expandedItems={expandedItems}
                onToggle={handleToggle}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default TreeSelect;
